import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Col } from 'react-bootstrap';
import Skeleton from '../skeleton/Skeleton';

/**
 * Individual Skill Component
 */
const SkillItem = memo(({ title, percentage }) => (
    <Col lg={3} md={6} className="wow fadeInUp">
        <p className="fw-500 text-start mb-2 text-dark">{title}</p>
        <div className="progress progress-sm mb-4">
            <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${percentage}%` }}
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${title} skill level: ${percentage}%`}
            />
        </div>
    </Col>
));

SkillItem.displayName = 'SkillItem';

SkillItem.propTypes = {
    title: PropTypes.string.isRequired,
    percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

/**
 * Skills Section Component
 * Renders skills with progress bars, loading and error states
 */
const SkillsSection = memo(({ skills, isLoading, error }) => {
    if (isLoading) {
        return <Skeleton type="skill" />;
    }

    if (error) {
        return (
            <div className="alert alert-warning" role="alert">
                <p className="mb-0">Unable to load skills data. Please try again later.</p>
            </div>
        );
    }

    if (!skills?.length) {
        return (
            <div className="text-muted text-center py-4">
                <p className="mb-0">No skills information available.</p>
            </div>
        );
    }

    return (
        <>
            {skills.map(({ id, title, percentage }) => (
                <SkillItem
                    key={id}
                    title={title}
                    percentage={percentage}
                />
            ))}
        </>
    );
});

SkillsSection.displayName = 'SkillsSection';

SkillsSection.propTypes = {
    skills: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            title: PropTypes.string.isRequired,
            percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
        })
    ),
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.string
};

SkillsSection.defaultProps = {
    skills: [],
    error: null
};

export default SkillsSection;